import onErrorCallback from '../analytics/on-error-callback';
import get from './public-functions/get';
import set from './public-functions/set';
import updateUserLocation from './public-functions/update-user-location';
import { toggleDialog } from '../state/redux/features/ui/thunks/toggle-dialog';
import createErrorModalStore from '../store/error-modal/create-store';

/**
 * The NikeShop class creates a singleton instance per load via nike-shop-client, exposing some public methods that client experiences can use to interface with apis related to shopping
 *
 * @class
 */
export default class NikeShop {
  #queryClient;

  constructor(props) {
    this.store = props.store;
    this.#queryClient = props.queryClient;
  }

  /**
   * @memberof NikeShop
   * @name NikeShop.getVersion
   * @returns {string} the page instantiated script's module version
   */
  getVersion = () => this.version;
  // maybe we change this to be an abstraction for our telemetry method in the future but for now it gives us a way to get
  // the right version each time.
  onErrorUniteCallback = () => onErrorCallback('nike-shop-unite-failure', this.getVersion());
  onErrorShellIdentityCallback = () =>
    onErrorCallback('nike-shop-shell-identity-failure', this.getVersion());

  /**
   * @memberof NikeShop
   * @name NikeShop.addToCart
   * @description add to a user cart with skuId or skuId and designViewId. For additional arguments see reference:
   * @see {@link https://github.com/nike-internal/dotcom.shop-modules/tree/main/packages/ux-tread-cart-redux#addtocart}
   *
   * @param {object} params - named parameters
   * @param {string} params.skuId - a valid sku uuid
   * @param {number} [params.quantity=1] - optional quantity for skuId to add to cart, defaults to 1
   * @param {string} [params.designViewId] -  optional unique identifiers for any customization items.
   * @param {Array} [params.designViewIds] - array of unique identifiers for any customization items.
   * @param {{ amount: number }} [params.giftCard] - optional gift card object with amount
   * @param {boolean} [params.isDigitalGiftCard] - optional is digital gift card
   * @param {boolean} [params.isPhysicalGiftCard] - optional is physical gift card
   * @param {string}  [params.offer] - optional UUID for exclusive access offer
   * @param {Array<{ id: string, type: string}>}  [params.offers] - optional offers for ExAx and NCP. This section is not intended for CN
   * @param {{ firstName: string, lastName: string }} [params.recipient] - optional object containing gift card recipient with firstName and lastName
   * @param {string} [params.recipientEmailAddress] - optional gift card recipient's email
   * @param {string} [params.shippingMethod] - optional shipping method for a gift card
   * @param {Array<{ id: string, instruction: { id: string, type: string }}>} [params.valueAddedServices] - optional vas info for item
   *
   * @returns {Promise} thenable result
   */

  /* For use by the Cart team only */
  __INTERNAL_USE__ = {
    errorModalStore: createErrorModalStore(),
    displayErrorModal: error => {
      this.__INTERNAL_USE__.errorModalStore.getState().setError(error);
    },
  };

  addToCart = async ({ quantity = 1, ...params }) => {
    const { default: addToCart } = await import(
      /* webpackChunkName: "addToCart" */ '../state/redux/features/cart/actions/add-to-cart'
    );
    return this.store.dispatch(addToCart({ quantity, ...params }));
  };
  addPromotionCode = async (params = {}) => {
    const { default: addPromotionCode } = await import(
      /* webpackChunkName: "addPromotionCode" */ '../state/redux/features/cart/actions/add-promotion-code'
    );
    return this.store.dispatch(addPromotionCode(params));
  };
  removePromotionCodes = async (params = {}) => {
    const { default: removePromotionCodes } = await import(
      /* webpackChunkName: "removePromotionCodes" */ '../state/redux/features/cart/actions/remove-promotion-codes'
    );
    return this.store.dispatch(removePromotionCodes(params));
  };
  /**
   * @memberof NikeShop
   * @name NikeShop.addToWishlist
   * @description add a product with a valid productId, and optional skuId to a user's default wishlist. If the default wishlist doesn't exist this function will ensure creating one prior to the add.
   *
   * @see {@link https://github.com/nike-internal/dotcom.shop-modules/tree/main/packages/wishlist-redux#initializewishlistandaddtocart}
   * @returns {Promise} thenable result
   */
  addToWishlist = async ({ skuId, productId, gtin, productCode }) => {
    const { default: addToWishlist } = await import(
      /* webpackChunkName: "addToWishlist" */ '../api/wishlists/add-to-wishlist'
    );
    return await addToWishlist({
      skuId,
      productId,
      gtin,
      productCode,
      queryClient: this.#queryClient,
    });
  };
  addToCartFromWishlist = async (params = {}) => {
    const { default: addToCartFromWishlist } = await import(
      /* webpackChunkName: "addToCartFromWishlist" */ '../state/redux/features/cart/actions/add-to-cart-from-wishlist'
    );
    return this.store.dispatch(addToCartFromWishlist(params));
  };
  removeItemsFromWishlist = async ({ ids }) => {
    const { default: removeFromWishlist } = await import(
      /* webpackChunkName: "removeItemsFromWishlist" */ '../api/wishlists/remove-from-wishlist'
    );
    return await removeFromWishlist({ ids, queryClient: this.#queryClient });
  };
  updateCartItem = async (params = {}) => {
    const { default: updateCartItem } = await import(
      /* webpackChunkName: "updateCartItem" */ '../state/redux/features/cart/actions/update-cart-item'
    );
    return this.store.dispatch(updateCartItem(params));
  };
  /**
   * @memberof NikeShop
   * @name NikeShop.fetchCart
   * @description fetch a user's cart
   * @see {@link https://github.com/nike-internal/dotcom.shop-modules/tree/main/packages/ux-tread-cart-redux#fetchCart}
   * @returns {Promise} thenable result
   */
  fetchCart = async (params = {}) => {
    const { default: fetchCart } = await import(
      /* webpackChunkName: "fetchCart" */ '../state/redux/features/cart/actions/fetch-cart'
    );
    return this.store.dispatch(fetchCart(params));
  };

  mergeCarts = async (params = {}) => {
    const { default: mergeCarts } = await import(
      /* webpackChunkName: "mergeCarts" */ '../state/redux/features/cart/actions/merge-carts'
    );
    return this.store.dispatch(mergeCarts(params));
  };

  /**
   * Please avoid using this as this will be shortly removed and internalized.
   * @deprecated
   * @param {*} params
   * @returns {Promise} thenable result
   */
  initializeWishlist = async () => {
    const { default: initializeWishlist } = await import(
      /* webpackChunkName: "initializeWishlist" */ '../state/redux/features/wishlist/actions/initialize-wishlist'
    );
    return this.store.dispatch(initializeWishlist({ queryClient: this.#queryClient }));
  };

  removeItemFromCart = async (params = {}) => {
    const { default: removeItemFromCart } = await import(
      /* webpackChunkName: "removeItemFromCart" */ '../state/redux/features/cart/actions/remove-item-from-cart'
    );
    return this.store.dispatch(removeItemFromCart(params));
  };

  getFulfillmentOfferings = async (params = {}) => {
    const { default: getFulfillmentOfferings } = await import(
      /* webpackChunkName: "getFulfillmentOfferings" */ './public-functions/get-fulfillment-offerings'
    );
    return getFulfillmentOfferings.call(this, params);
  };

  /**
   * @memberof NikeShop
   * @name NikeShop.getFulfillmentOfferingsCartReviews
   * @description returns a Promise that handles fulfilment offerings + cart reviews
   *
   * @param {object} [params={}] - optional named parameters
   * @param {number} [params.distance = 50] - radius distance to search for stores, defaults to 50 miles for MVP
   * @param {string} params.email - users email to go with the async request
   * @param {boolean} [params.isSearch = false] - whether or not to search an area for stores for pickup
   * @param {Array.<string>} [params.offeringTypes = ['SHIP', 'PICKUP', 'DIGITAL']] - required list of fulfillment offering types to be included. SHIP, PICKUP, DIGITAL, or INSTORE
   * @param {number} [params.timeout = 30000] - optional time to timeout underlying async job api call
   * @returns {Promise}  Result of fulfillment offerings and cart reviews
   */
  getFulfillmentOfferingsCartReviews = async (params = {}) => {
    const { default: fetchFulfillmentOfferingsCartReviews } = await import(
      /* webpackChunkName: "getFulfillmentOfferingsCartReviews" */ '../state/redux/actions/fetch-fulfillment-offerings-cart-reviews'
    );
    return this.internalStore.dispatch(fetchFulfillmentOfferingsCartReviews(params));
  };

  /**
   * /**
   * @memberof NikeShop
   * @name NikeShop.determineDefaultShippingOption
   * @description uses persisted fulfillment offerings data for shipping option
   *
   * @param {string} params.skuId
   *
   * @typedef {Object} ShippingOption
   * @property {Object} fulfillmentAttributes
   * @property {string} fulfillmentAnnotation
   * @property {string} minShipDate
   * @property {string} maxShipDate
   * @property {number} price
   * @property {string} timeZone
   *
   * @returns {ShippingOption}
   */
  determineDefaultShippingOption = async (params = {}) => {
    const { default: determineDefaultShippingOption } = await import(
      /* webpackChunkName: "determineDefaultShippingOption" */ '../nike-shop/public-functions/determine-default-shipping-option'
    );
    return determineDefaultShippingOption(params);
  };

  navigateToCheckout = async (params = {}) => {
    const { default: navigateToCheckout } = await import(
      /* webpackChunkName: "navigateToCheckout" */ '../state/redux/actions/navigate-to-checkout'
    );
    return this.internalStore.dispatch(navigateToCheckout(params));
  };

  toggleDialog = (params = {}) => {
    return toggleDialog.call(this, params);
  };

  /**
   * @public
   * @memberof NikeShop
   * @name NikeShop.toggleAddToCartModal
   * @returns {Promise} Promise that resolves with add to cart modal action
   */
  toggleAddToCartModal = async (params = {}) => {
    const { default: toggleAddToCartModal } = await import(
      /* webpackChunkName: "toggleAddToCartModal" */ '../state/redux/features/ui/thunks/toggle-add-to-cart-modal'
    );
    return toggleAddToCartModal.call(this, params);
  };

  fetchProductDetails = async (params = {}) => {
    const { default: fetchProductDetails } = await import(
      /* webpackChunkName: "fetchProductDetails" */ '../state/redux/features/normalized/actions/fetch-product-details'
    );
    return this.internalStore.dispatch(fetchProductDetails(params));
  };

  getProductsInFavorites = async products => {
    const { default: getProductsInFavorites } = await import(
      /* webpackChunkName: "getProductsInFavorites" */ './public-functions/get-products-in-favorites'
    );
    return getProductsInFavorites(products);
  };

  get = () => {
    const privateVars = { queryClient: this.#queryClient };
    // allows access to NikeShop's `this` context
    return get.call(this, privateVars);
  };

  set = set.bind(this);

  updateStore = ({ store, internalStore }) => {
    this.store = store || this.store;
    this.internalStore = internalStore || this.internalStore;
  };

  // allows access to NikeShop's `this` context
  updateUserLocation = updateUserLocation.bind(this);
}
